import { AffectedQuotesResponse } from "api/tenant";
import { Sizes } from "enums/sizes";
import { useDialog } from "hooks/useDialog";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AffectedQuotesConfirmContent } from "./AffectedQuotesConfirmContent";

export type EntityType = 'person' | 'realEstate' | 'tenant' | 'guarantor' | 'parc';

interface UseAffectedQuotesConfirmProps {
  entityType: EntityType;
  fetchAffectedQuotes: () => Promise<AffectedQuotesResponse | undefined>;
  onConfirm: () => Promise<void>;
  isEditMode: boolean;
}

export const useAffectedQuotesConfirm = ({
  entityType,
  fetchAffectedQuotes,
  onConfirm,
  isEditMode
}: UseAffectedQuotesConfirmProps) => {
  const { openDialog, setDialogActions, closeDialog } = useDialog();
  const { t } = useTranslation();

  const showConfirmationDialog = useCallback(async () => {
    if (!isEditMode) {
      await onConfirm();
      return;
    }

    const affectedQuotes = await fetchAffectedQuotes();

    if (!affectedQuotes || affectedQuotes.count === 0) {
      await onConfirm();
      return;
    }

    openDialog(
      {
        title: t('AFFECTED_QUOTES.DIALOG_TITLE'),
        size: Sizes["3xl"],
      },
      <AffectedQuotesConfirmContent
        affectedQuotes={affectedQuotes}
        entityType={entityType}
        onNavigate={closeDialog}
      />
    );

    setDialogActions({
      closeLabel: t('COMMON.CANCEL'),
      submitLabel: t('COMMON.CONFIRM'),
      submitDisabled: false,
      onSubmit: async () => {
        closeDialog();
        await onConfirm();
      }
    });
  }, [isEditMode, fetchAffectedQuotes, onConfirm, openDialog, setDialogActions, closeDialog, t, entityType]);

  return {
    showConfirmationDialog
  };
};
